import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	ClearSession,
	ClearSessionSuccess,
	ESessionActions,
	GenerateTokenAndNavigateToModule,
	GenerateTokenAndNavigateToModuleSuccess,
	GetDepartments,
	GetDepartmentsSuccess,
	GetHubChain,
	GetHubChainSuccess,
	GetSession,
	GetSessionSuccess,
	GetTenants,
	GetTenantsSuccess,
	Login,
	LoginSuccess,
	NavigateToModule,
	PunchLogin,
	PunchLoginSuccess,
	PunchLogout,
	RegisterTenant,
	RegisterTenantSuccess,
	SaveUserProfile,
	SendForgotPassword,
	SendForgotPasswordSuccess,
	SetNewPassword,
	SetNewPasswordSuccess,
	SetNewUser,
	SetNewUserSuccess,
	SetTenant,
	SetUser,
	SetUserType,
	SubscribeWebPushNotifications,
	SwitchDepartment,
	UnsubscribeWebPushNotifications,
	UpdateCurrentUser,
} from '@app/core/store/actions/session.actions';
import {AuthService, UserType} from '@app/core/services/auth.service';
import {Tenant} from '@app/core/models/tenant.model';
import {Router} from '@angular/router';
import {TenantService} from '@app/modules/shared/services/tenant.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {PunchUserService} from '@app/modules/shared/services/punchUser.service';
import {Department} from '@app/core/models/department.model';
import {HttpService} from '@app/core/services/http.service';
import {EQOEnvironment, EQOHubApplication, EQOLogLevel, QOHubService, QOLogService} from '@quickorder/angular-web-lib';
import {TranslateService} from '@ngx-translate/core';
import {SnackBar} from '@app/core/services/snackbar.service';
import {SegmentService} from '@app/core/services/segment.service';
import {GetPunchDepartment, GetPunchTenant} from '@app/core/store/actions/punch-clock.actions';
import {LocalStorageService} from '@app/core/services/local-storage.service';
import {IQOHubChain} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-hub-chain.interface';
import {getCurrentState} from '@app/core/helpers';
import {IAppState} from '@app/core/store/state/app.state';
import {Store} from '@ngrx/store';
import {environment} from '@env/environment';

@Injectable()
export class SessionEffects {

	@Effect()
	public onGetSession$ = this.actions$.pipe(
		ofType<GetSession>(ESessionActions.GetSession),
		switchMap((action: GetSession) => from(this.authService.getUserDetails()).pipe(
			switchMap((response) => {
					this.localStorageService.storeAuthTenantId(response.tenant.id);

					return [
						new SetTenant(response.tenant),
						new GetSessionSuccess(response.user, response.department, response.departments),
					];
				},
			),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onSwitchDepartment$ = this.actions$.pipe(
		ofType<SwitchDepartment>(ESessionActions.SwitchDepartment),
		switchMap((action: SwitchDepartment) => from(this.authService.switchDepartmentNew(action.departmentId)).pipe(
			switchMap((res) => {
				localStorage.setItem('user', res.user_details.serialize());
				localStorage.setItem('selectedDepartmentId', String(action.departmentId));
				this.localStorageService.storeAuthDepartmentId(action.departmentId);
				location.reload();

				return [];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onSetUser$ = this.actions$.pipe(
		ofType<SetUser>(ESessionActions.SetUser),
		switchMap((action: SetUser) => {
			this.segmentService.identifyUser();

			return [];
		}),
	);

	@Effect()
	public onSetNewPassword$ = this.actions$.pipe(
		ofType<SetNewPassword>(ESessionActions.SetNewPassword),
		switchMap((action: SetNewPassword) => from(this.authService.resetPassword(action.email, action.token, action.password)).pipe(
			switchMap((res) => {
				if (window['PasswordCredential']) {
					const cred = new window['PasswordCredential']({
						id: action.email,
						password: action.password,
					});
					navigator['credentials'].store(cred);
				}

				this.router.navigate(['./login'], {queryParams: {email: action.email}});

				return [
					new SetNewPasswordSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onPunchLogin$ = this.actions$.pipe(
		ofType<PunchLogin>(ESessionActions.PunchLogin),
		switchMap((action: PunchLogin) => from(this.authService.punchLogin(action.department_id, action.password)).pipe(
			switchMap((res: any) => {
				this.authService.setAccessToken(res.access_token);
				this.localStorageService.storeAuthDepartmentId(res.department.id);

				return [
					new SetUserType(UserType.PUNCH_USER),
					new PunchLoginSuccess(),
					new GetPunchDepartment(res.department.id),
					new GetPunchTenant(action.tenant_id),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onSetNewUser$ = this.actions$.pipe(
		ofType<SetNewUser>(ESessionActions.SetNewUser),
		switchMap((action: SetNewUser) => from(this.authService.setNewUser(
			action.email,
			action.password,
			action.sign_up_token,
			action.phone_number,
			action.birth_date,
			action.phone_ext,
			action.language_id,
		)).pipe(
			switchMap((res: any) => {
				if (window['PasswordCredential']) {
					const cred = new window['PasswordCredential']({
						id: action.email,
						password: action.password,
					});
					navigator['credentials'].store(cred);
				}
				this.router.navigate(['./login'], {queryParams: {email: action.email}});

				return [
					new SetNewUserSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onSendForgotPassword$ = this.actions$.pipe(
		ofType<SendForgotPassword>(ESessionActions.SendForgotPassword),
		switchMap((action: SendForgotPassword) => from(this.authService.sendForgotPassword(action.email)).pipe(
			switchMap((res: any) =>
				[
					new SendForgotPasswordSuccess(),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetTenants$ = this.actions$.pipe(
		ofType<GetTenants>(ESessionActions.GetTenants),
		switchMap((action: GetTenants) => from(this.authService.getUserTenantsToLogin(action.email, action.password)).pipe(
			switchMap((tenants: Tenant[]) => {

				if (tenants.length === 0) {
					this.snackbar.newError('USER.INVALID_EMAIL');
				}

				if (action.password !== null && tenants.length === 1) {
					return [
						new Login({email: action.email, password: action.password}, tenants[0]),
					];
				}

				return [
					new GetTenantsSuccess(tenants),
				];
			}),
			catchError(error => of(
				new ClearSession(),
				new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetTenantsList$ = this.actions$.pipe(
		ofType<GetTenants>(ESessionActions.GetTenantsList),
		switchMap((action: GetTenants) => from(this.authService.getUserTenantsList(action.email)).pipe(
			switchMap((tenants: Tenant[]) => {

				if (tenants.length === 0) {
					this.snackbar.newError('USER.INVALID_EMAIL');
				}

				return [
					new GetTenantsSuccess(tenants),
				];
			}),
			catchError(error => of(
				new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetDepartments$ = this.actions$.pipe(
		ofType<GetDepartments>(ESessionActions.GetDepartments),
		switchMap((action: GetDepartments) => from(this.punchUserService.getDepartmentsForTenant()).pipe(
			switchMap((res: Department[]) =>
				[
					new GetDepartmentsSuccess(res),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onLogin$ = this.actions$.pipe(
		ofType<Login>(ESessionActions.Login),
		switchMap((action: Login) => from(this.authService.login(action.login, action.selectedTenant)).pipe(
			switchMap((res: any) => {
				const prevUrl = this.router.parseUrl(this.router.url).queryParams['prev'];
				this.localStorageService.storeAuthDepartmentId(res.user_details.selected_department_id);
				if (prevUrl) {
					this.router.navigate([prevUrl]);
				} else if (this.authService.currentUser.permission_level === 2) {
					(this.authService.tenant.getting_started)
						? this.router.navigate(['./home/kom-i-gang'])
						: this.router.navigate(['./home/oversigt']);
				} else {
					this.router.navigate(['./home/oversigt']);
				}

				return [
					new LoginSuccess(),
					new GetSession(),
				];
			}),
			catchError(error =>
				of(
					new ClearSessionSuccess(),
					new LoadError(error, action))),
		)),
	);

	@Effect()
	public onClearSession$ = this.actions$.pipe(
		ofType<ClearSession>(ESessionActions.ClearSession),
		switchMap((action: ClearSession) => from(this.authService.logout()).pipe(
			switchMap((res: any) => {
				localStorage.clear();
				this.httpService.access_token = undefined;
				this.authService.setAccessToken(undefined);
				this.router.navigate(['/login']);

				return [
					new ClearSessionSuccess(),
				];
			}),
			catchError(error =>
				of(
					new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPunchLogout$ = this.actions$.pipe(
		ofType<PunchLogout>(ESessionActions.PunchLogout),
		switchMap((action: PunchLogout) => from(this.authService.punchLogout()).pipe(
			switchMap((res: any) => {
				this.router.navigate(['/punch-login']);
				localStorage.clear();

				return [
					new ClearSessionSuccess(),
				];
			}),
			catchError(error =>
				of(
					new LoadError(error, action))),
		)),
	);
	@Effect()
	public onSaveUserProfile$ = this.actions$.pipe(
		ofType<SaveUserProfile>(ESessionActions.SaveUserProfile),
		switchMap((action: SaveUserProfile) => from(this.authService.saveUserProfile(action.data)).pipe(
			switchMap((res: any) => {

				if (action.emailChanged) {
					this.logService.logContent({
						level: EQOLogLevel.INFO,
						title: this.translate.instant('SNACKBAR.DONE'),
						description: this.translate.instant('EMAIL_VERIFICATION.EMAIL_VERIFICATION_REQUIRED'),
						forceDuration: 999999,
						actions: [],
					});
				} else {
					this.logService.logContent({
						title: this.translate.instant('SNACKBAR.YOUR_SETTINGS_SAVED'),
						actions: [],
						level: EQOLogLevel.SUCCESS,
					});
				}

				return [
					new UpdateCurrentUser(action.data),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onSetUserType$ = this.actions$.pipe(
		ofType<SetUserType>(ESessionActions.SetUserType),
		switchMap((action: SetUserType) => {
				localStorage.setItem('userType', action.userType);

				return [];
			},
		),
	);

	@Effect()
	public onSubscribeWebPushNotifications$ = this.actions$.pipe(
		ofType<SubscribeWebPushNotifications>(ESessionActions.SubscribeWebPushNotifications),
		switchMap((action: SubscribeWebPushNotifications) => from(this.authService.subscribeWebPushNotifications()).pipe(
			switchMap(() => []),
		)),
		catchError(() => {
			this.snackbar.newError('SNACKBAR.ERROR_IN_NOTIFICATIONS');

			return [];
		}),
	);

	@Effect()
	public onUnsubscribeWebPushNotifications$ = this.actions$.pipe(
		ofType<UnsubscribeWebPushNotifications>(ESessionActions.UnsubscribeWebPushNotifications),
		switchMap((action: UnsubscribeWebPushNotifications) => from(this.authService.unsubscribeWebPushNotifications()).pipe(
			switchMap(() => []),
		)),
	);

	@Effect()
	public onRegisterTenant$ = this.actions$.pipe(
		ofType<RegisterTenant>(ESessionActions.RegisterTenant),
		switchMap((action: RegisterTenant) => from(this.tenantService.register(action.payload)).pipe(
			switchMap((tenant: Tenant) => [
				new RegisterTenantSuccess(),
			]),
			catchError(error => of(new LoadError(error, action, true))),
		)),
	);

	// qo-hub
	@Effect()
	public onGetHubChain = this.actions$.pipe(
		ofType<GetHubChain>(ESessionActions.GetHubChain),
		switchMap((action: GetHubChain) => from(this.authService.getHubChain()).pipe(
			switchMap((hubChain: IQOHubChain) => [
				new GetHubChainSuccess(hubChain),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onNavigateToModule = this.actions$.pipe(
		ofType<NavigateToModule>(ESessionActions.NavigateToModule),
		switchMap((action: NavigateToModule) => {

			const hubApplication: EQOHubApplication = this.hubService.mapQOModuleToHubApplication(action.module);

			const hubChain = getCurrentState(this.store).session.hub_chain;

			if (!hubChain || this.hubService.hasApplicationInChain(hubChain?.venues, hubApplication)) {
				window.open(this.hubService.getUrlForApplication(hubApplication, environment.qoEnvironment as EQOEnvironment), '_blank');

				return [];
			} else {

				const tenantId: number = getCurrentState(this.store).session.department.id;

				return [
					new GenerateTokenAndNavigateToModule(hubApplication, tenantId),
				];

			}
		}),
	);

	@Effect()
	public onGenerateTokenAndNavigateToModule = this.actions$.pipe(
		ofType<GenerateTokenAndNavigateToModule>(ESessionActions.GenerateTokenAndNavigateToModule),
		switchMap((action: GenerateTokenAndNavigateToModule) => from(this.authService.getHubRegistrationToken(action.tenantId)).pipe(
			switchMap((data: { token: string }) => {

				const email: string = getCurrentState(this.store).session.hub_chain.email;
				const baseUrl: string = this.hubService.getUrlForApplication(action.application, environment.qoEnvironment as EQOEnvironment);

				const finalUrl: string = baseUrl + 'register?hubToken=' + data.token + '&email=' + email;

				window.open(finalUrl, '_blank');

				return [
					new GenerateTokenAndNavigateToModuleSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private store: Store<IAppState>,
		private actions$: Actions,
		private authService: AuthService,
		private segmentService: SegmentService,
		private router: Router,
		private tenantService: TenantService,
		private interService: InternationalizationService,
		private punchUserService: PunchUserService,
		private httpService: HttpService,
		private logService: QOLogService,
		private translate: TranslateService,
		private snackbar: SnackBar,
		private localStorageService: LocalStorageService,
		private hubService: QOHubService,
	) {
	}
}
